import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Bulk selection in the conversation list and search results: checked ids, the anchor of a
 * shift-click range, and whether "everything matching" was chosen (beyond the loaded rows).
 * The pure transitions are tested without a DOM; `useSelection` keeps one per view and starts
 * empty whenever the view (scope, filter, search) changes.
 */
export interface Selection {
  ids: ReadonlySet<string>;
  anchor: number | null;
  /** Every item the view's query matches, not only the loaded ones. */
  all: boolean;
}

export const NO_SELECTION: Selection = { ids: new Set(), anchor: null, all: false };

/**
 * A click on row `index`: it flips; with shift and an anchor, every row between the anchor and
 * it takes the clicked row's new state. The clicked row becomes the anchor.
 */
export function toggleAt(sel: Selection, order: readonly string[], index: number, shift: boolean): Selection {
  const id = order[index];
  if (id === undefined) return sel;
  const checked = !sel.ids.has(id);
  const next = new Set(sel.ids);
  const from = shift && sel.anchor !== null ? Math.min(sel.anchor, index) : index;
  const to = shift && sel.anchor !== null ? Math.max(sel.anchor, index) : index;
  for (let i = from; i <= to; i++) {
    const row = order[i];
    if (row === undefined) continue;
    if (checked) next.add(row);
    else next.delete(row);
  }
  return { ids: next, anchor: index, all: false };
}

/** Every loaded row (or none, when they all are already). */
export function toggleAllLoaded(sel: Selection, order: readonly string[]): Selection {
  const everything = order.length > 0 && order.every((id) => sel.ids.has(id));
  return everything ? NO_SELECTION : { ids: new Set(order), anchor: null, all: false };
}

/** Drops selected ids that left the list (archived meanwhile, or another page). */
export function prune(sel: Selection, order: readonly string[]): Selection {
  if (sel.all) return sel;
  const present = new Set(order);
  const kept = [...sel.ids].filter((id) => present.has(id));
  return kept.length === sel.ids.size ? sel : { ...sel, ids: new Set(kept) };
}

export function useSelection(viewKey: string, order: readonly string[]) {
  const [sel, setSel] = useState<Selection>(NO_SELECTION);
  useEffect(() => setSel(NO_SELECTION), [viewKey]);
  const orderKey = order.join("\n");
  useEffect(() => setSel((s) => prune(s, orderKey === "" ? [] : orderKey.split("\n"))), [orderKey]);
  const toggle = useCallback((index: number, shift: boolean) => setSel((s) => toggleAt(s, order, index, shift)), [order]);
  const toggleAll = useCallback(() => setSel((s) => toggleAllLoaded(s, order)), [order]);
  const selectMatching = useCallback(() => setSel({ ids: new Set(order), anchor: null, all: true }), [order]);
  const clear = useCallback(() => setSel(NO_SELECTION), []);
  return useMemo(() => ({ sel, toggle, toggleAll, selectMatching, clear }), [sel, toggle, toggleAll, selectMatching, clear]);
}

export type SelectionState = ReturnType<typeof useSelection>;
